import { useState, useEffect } from "react";
import axios from "axios";
import { useTheme } from "../../context/ThemeContext";
import { useUser } from "../../context/UserContext";
import api from "../../api/Api";

const fmt = (n) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function StatCard({ label, icon, value, sub, color, t }) {
  return (
    <div
      style={{
        background: t.surface,
        borderRadius: 18,
        padding: "1rem 1.2rem",
        flex: 1,
        border: `1px solid ${t.border}`,
        boxShadow: t.shadow,
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: t.textMuted,
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span style={{ fontSize: 15 }}>{icon}</span> {label}
      </div>

      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: color || t.text,
        }}
      >
        {value}
      </div>

      <div
        style={{
          fontSize: 12,
          color: t.textMuted,
          marginTop: 4,
        }}
      >
        {sub}
      </div>
    </div>
  );
}

function Badge({ type, isDarkMode }) {
  const styles = {
    credit: {
      bg: isDarkMode ? "#052e1a" : "#dcfce7",
      color: isDarkMode ? "#4ade80" : "#166534",
    },
    debit: {
      bg: isDarkMode ? "#1e1b4b" : "#ede9fe",
      color: isDarkMode ? "#a78bfa" : "#5b21b6",
    },
  };

  const s = styles[type];

  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        fontSize: 11,
        fontWeight: 700,
        padding: "4px 11px",
        borderRadius: 30,
        textTransform: "capitalize",
      }}
    >
      {type}
    </span>
  );
}

const TransactionDashboard = () => {
  const { isDarkMode } = useTheme();
  const { user } = useUser();

  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const PER_PAGE = 10;

  const t = {
    bg: isDarkMode ? "#0d0d0d" : "#f8f9fa",
    surface: isDarkMode ? "#1a1a1a" : "#ffffff",
    surfaceAlt: isDarkMode ? "#222222" : "#f5f5f5",
    border: isDarkMode ? "#2e2e2e" : "#e5e7eb",
    text: isDarkMode ? "#f3f4f6" : "#111827",
    textMuted: isDarkMode ? "#9ca3af" : "#6b7280",
    rowHover: isDarkMode ? "#242424" : "#f9fafb",
    accent: isDarkMode ? "#6366f1" : "#4f46e5",
    gold: "#D4AF37",
    green: isDarkMode ? "#4ade80" : "#15803d",
    debitColor: isDarkMode ? "#a78bfa" : "#7c3aed",
    btnBorder: isDarkMode ? "#3a3a3a" : "#d1d5db",
    shadow: isDarkMode
      ? "0 4px 18px rgba(0,0,0,0.4)"
      : "0 4px 18px rgba(0,0,0,0.06)",
  };

  // API CALL
  const fetchTransactions = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const params = {
        page,
        per_page: PER_PAGE,
      };

      if (search) {
        params.search = search;
      }

      if (filter !== "all") {
        params.type = filter;
      }

      const response = await api.get(
        `/wallet-transactions`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const res = response.data;

      setTransactions(res.data.transactions || []);
      setSummary(res.summary || {});
      setLastPage(res.data.last_page || 1);
      setTotal(res.data.total || 0);

    } catch (error) {
      console.error("Transaction API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, filter]);

  // SEARCH WITH DELAY
  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(1);
      fetchTransactions();
    }, 500);

    return () => clearTimeout(delay);
  }, [search]);

  const handleFilter = (f) => {
    setFilter(f);
    setPage(1);
  };

  const walletBalance = parseFloat(
    user?.WALLET?.balance ?? 0
  ).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const filterBtnStyle = (f) => ({
    padding: "8px 15px",
    fontSize: 13,
    fontWeight: 600,
    border: `1px solid ${filter === f ? t.accent : t.btnBorder
      }`,
    borderRadius: 10,
    background:
      filter === f
        ? isDarkMode
          ? "#312e81"
          : "#4f46e5"
        : t.surface,
    color: filter === f ? "#fff" : t.textMuted,
    cursor: "pointer",
  });

  const pageBtnBase = {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: `1px solid ${t.btnBorder}`,
    background: t.surface,
    color: t.textMuted,
    fontSize: 13,
    cursor: "pointer",
  };

  return (
    <div
      style={{
        background: t.bg,
        minHeight: "100vh",
        padding: "2rem 1rem",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
        }}
      >
        <h1
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: t.text,
          }}
        >
          Transaction History
        </h1>

        <span
          style={{
            background: isDarkMode ? "#1f2937" : "#111827",
            border: `1px solid ${t.gold}`,
            borderRadius: 12,
            padding: "8px 16px",
            color: "#fff",
          }}
        >
          Balance:{" "}
          <strong style={{ color: t.gold }}>
            ₹{walletBalance}
          </strong>
        </span>
      </div>

      {/* Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit,minmax(220px,1fr))",
          gap: 14,
          marginBottom: "1.5rem",
        }}
      >
        <StatCard
          t={t}
          icon="⇄"
          label="Total Transactions"
          value={summary.total_transactions || 0}
          sub="All transactions"
        />

        <StatCard
          t={t}
          icon="↓"
          label="Total Credit"
          value={fmt(summary.total_credit)}
          sub="Credited amount"
          color={t.green}
        />

        <StatCard
          t={t}
          icon="↑"
          label="Total Debit"
          value={fmt(summary.total_debit)}
          sub="Debited amount"
          color={t.debitColor}
        />
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search transactions..."
          style={{
            flex: 1,
            minWidth: 250,
            padding: "10px 14px",
            borderRadius: 12,
            border: `1px solid ${t.border}`,
            background: t.surface,
            color: t.text,
          }}
        />

        {["all", "credit", "debit"].map((f) => (
          <button
            key={f}
            onClick={() => handleFilter(f)}
            style={filterBtnStyle(f)}
          >
            {f.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Table */}
      <div
        style={{
          background: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: 20,
          overflowX: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr style={{ background: t.surfaceAlt }}>
              <th style={thStyle(t)}>Description</th>
              <th style={thStyle(t)}>Type</th>

              <th style={{ ...thStyle(t), textAlign: "right" }}>
                Amount
              </th>
              <th style={{ ...thStyle(t), textAlign: "right" }}>
                Clear
              </th>
            </tr>
          </thead>


          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", padding: "2rem", color: t.textMuted }}>
                  No transactions found
                </td>
              </tr>
            ) : (
              transactions.map((tx, idx) => (
                <tr
                  key={tx.id}
                  style={{ borderBottom: idx < transactions.length - 1 ? `1px solid ${t.border}` : "none" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = t.rowHover)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {/* Description */}
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          background: tx.type === "credit"
                            ? isDarkMode ? "#052e1a" : "#dcfce7"
                            : isDarkMode ? "#1e1b4b" : "#ede9fe",
                          color: tx.type === "credit"
                            ? isDarkMode ? "#4ade80" : "#166534"
                            : isDarkMode ? "#a78bfa" : "#5b21b6",
                        }}
                      >
                        {tx.type === "credit" ? "↓" : "↑"}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{tx.description}</div>
                        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{tx.created_at}</div>
                      </div>
                    </div>
                  </td>


                  {/* Type */}
                  <td style={{ padding: "14px 16px" }}>
                    <Badge type={tx.type} isDarkMode={isDarkMode} />
                  </td>

                  {/* Amount */}
                  <td
                    style={{
                      padding: "14px 16px",
                      textAlign: "right",
                      fontSize: 14,
                      fontWeight: 700,
                      color: tx.type === "credit" ? t.green : t.debitColor,
                    }}
                  >
                    {tx.type === "credit" ? "+" : "−"}{fmt(tx.amount)}
                  </td>

                  <td
                    style={{
                      padding: "14px 16px",
                      textAlign: "right",
                      fontSize: 14,
                      fontWeight: 700,
                      color: tx.type === "credit" ? t.green : t.debitColor,
                    }}
                  >
                    {tx.type === "credit" ? "+" : "−"}{fmt(tx.balance_after)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "1rem",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: 13,
            color: t.textMuted,
          }}
        >
          Showing {transactions.length} of {total}
        </span>

        <div style={{ display: "flex", gap: 6 }}>
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            style={{
              ...pageBtnBase,
              opacity: page === 1 ? 0.5 : 1,
            }}
          >
            ‹
          </button>

          {Array.from(
            { length: lastPage },
            (_, i) => i + 1
          ).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                ...pageBtnBase,
                background:
                  p === page
                    ? isDarkMode
                      ? "#312e81"
                      : "#4f46e5"
                    : t.surface,
                color: p === page ? "#fff" : t.textMuted,
              }}
            >
              {p}
            </button>
          ))}

          <button
            disabled={page === lastPage}
            onClick={() => setPage(page + 1)}
            style={{
              ...pageBtnBase,
              opacity: page === lastPage ? 0.5 : 1,
            }}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
};

const thStyle = (t) => ({
  padding: "14px 16px",
  fontSize: 12,
  fontWeight: 700,
  color: t.textMuted,
  textAlign: "left",
  borderBottom: `1px solid ${t.border}`,
});

const tdStyle = (t) => ({
  padding: "14px 16px",
  fontSize: 13,
  color: t.text,
  borderBottom: `1px solid ${t.border}`,
});

export default TransactionDashboard;
